'use client';

import { useCallback, useState } from 'react';

import Image from 'next/image';

import { cn } from '@lib/utils/cn';
import { NestedRepeaterLink, TransformedImageType } from '@types';

import { Text } from '../../components/text';

/**
 * SubMenuImage Component
 *
 * Renders an image section in a submenu with transition effects between images.
 * When no image is present, displays a logo and menu label instead.
 *
 * @component
 * @param {Object} props
 * @param {NestedRepeaterLink} props.menuStack - The current menu item data
 * @param {'left' | 'right' | undefined} props.subMenuImagePosition - Position of the submenu image
 * @param {TransformedImageType | null} props.logo - Logo image to display when no current image
 * @param {TransformedImageType | null} props.currentImage - Currently active submenu image
 * @param {string} props.sizes - Image sizes attribute for responsive images
 */
export const SubMenuImage = ({
  subMenuImagePosition = 'left',
  menuStack,
  logo,
  currentImage,
  sizes,
}: {
  menuStack: NestedRepeaterLink;
  subMenuImagePosition: 'left' | 'right' | undefined;
  logo: TransformedImageType | null;
  currentImage: TransformedImageType | null;
  sizes: string;
}) => {
  // Use useCallback to memoize the animation end handler
  const handleAnimationEnd = useCallback(() => {
    setPreviousImage(currentImage);
  }, [currentImage]);

  // Track the previous image to create transition effects between images
  const [previousImage, setPreviousImage] = useState<TransformedImageType | null>(null);

  // Memoize the position-based classes
  const containerClasses = cn('border-border relative hidden flex-col justify-end gap-1 @xl:flex', {
    'items-end border-l pl-6 @xl:order-last': subMenuImagePosition === 'right',
    'border-r pr-6': subMenuImagePosition === 'left',
    'overflow-hidden rounded-lg border-none': currentImage,
  });

  const ImageLayer = ({
    image,
    className,
    onAnimationEnd,
  }: {
    image: TransformedImageType;
    className: string;
    onAnimationEnd?: () => void;
  }) => (
    <Image
      fill
      {...image}
      sizes={sizes}
      className={cn('relative object-cover', className)}
      onAnimationEnd={onAnimationEnd}
    />
  );

  return (
    <div className={containerClasses}>
      {/* Current Image Layer */}
      {currentImage && currentImage?.src !== previousImage?.src && (
        <ImageLayer
          image={currentImage}
          className="animate-in fade-in-0 z-10 duration-150"
          onAnimationEnd={handleAnimationEnd}
        />
      )}

      {/* Previous Image Layer */}
      {currentImage && previousImage?.src && <ImageLayer image={previousImage} className="z-5" />}

      {/* Fallback Content */}
      {/* Shows logo and menu label when no image is present */}
      <div
        className={cn('absolute inset-0 z-0 flex flex-col justify-end transition-opacity duration-300', {
          'items-end': subMenuImagePosition === 'right',
          'opacity-100': !currentImage,
          'opacity-0': currentImage,
        })}
      >
        {/* Large watermark-style logo in background */}
        <div
          className={cn('pointer-events-none absolute flex size-96 translate-y-32 opacity-5', {
            'translate-x-32': subMenuImagePosition === 'right',
            '-translate-x-32': subMenuImagePosition === 'left',
          })}
        >
          {logo && <Image fill {...logo} sizes={sizes} className="object-contain" />}
        </div>
        <Text element={'h3'}>{menuStack.label}</Text>
      </div>
    </div>
  );
};
